import { Link } from "react-router-dom";
import { SiteFooter } from "@/components/SiteFooter";
import { company, identityLine } from "@/lib/company";

const UPDATED = "17 September 2026";

/**
 * Required by both app stores before a listing can go live, and required by
 * POPIA (South Africa's data protection law) regardless. This reflects what
 * the platform actually does today -- see the endpoints and third parties
 * named below against services/api and apps/customer before editing either
 * without updating this page to match.
 *
 * This is a starting draft, not a substitute for legal review. Have a
 * lawyer check it against POPIA's specific requirements (Information
 * Officer registration, the exact rights-request process) before treating
 * it as final.
 */
export function Privacy() {
  return (
    <>
    <div className="mx-auto max-w-2xl px-4 py-12 text-ink-800">
      <Link to="/" className="text-sm font-medium text-brand">
        ← WeShip
      </Link>
      <h1 className="mt-6 text-3xl font-semibold tracking-tight text-ink-900">Privacy Policy</h1>
      <p className="mt-1 text-sm text-ink-400">Last updated {UPDATED}</p>

      <div className="prose-sm mt-8 space-y-6 text-[15px] leading-relaxed">
        <p>
          This policy covers the WeShip web portal and the WeShip mobile app, and explains what we
          collect, why, and who we share it with. If you have questions or want to exercise any of
          the rights below, contact us at{" "}
          <a className="text-brand" href={`mailto:${company.privacyEmail}`}>
            {company.privacyEmail}
          </a>
          .
        </p>
        <p>
          The responsible party, in POPIA's terms, is {identityLine()}. Our full contact details are
          at the foot of every page.
        </p>

        <section>
          <h2 className="text-lg font-semibold text-ink-900">What we collect</h2>
          <ul className="mt-2 list-disc space-y-1.5 pl-5">
            <li><strong>Account details</strong> — name, email, phone, company name, and password (stored as a hash, never in plain text).</li>
            <li><strong>Addresses</strong> — collection and delivery addresses for your shipments, including a contact name, phone number, and any instructions you add. This includes addresses for people you are shipping to, not only your own.</li>
            <li><strong>Shipment details</strong> — parcel dimensions and weight, declared value, service level chosen, and the resulting waybill and tracking history.</li>
            <li><strong>Payment and wallet records</strong> — top-up amounts, ledger entries, and transaction status. We do not see or store your card details: those are entered directly with our payment processor, BobPay.</li>
            <li><strong>Location</strong> — only if you grant permission, and only to help you search for an address faster. We do not track your location in the background.</li>
            <li><strong>Device push token</strong> (mobile app only) — a token that lets us send you shipment and wallet notifications. Tied to your account, not to your identity beyond that.</li>
            <li><strong>Usage and log data</strong> — IP address, device/browser type, and request timestamps, kept for security and fraud prevention.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-ink-900">Why we collect it</h2>
          <p className="mt-2">
            To create and manage your account, generate quotes and book shipments, process payments
            and keep your wallet accurate, send you updates about your shipments and account, respond
            to support requests, and detect and prevent fraud or abuse. We do not sell your personal
            information, and we do not use it for advertising.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-ink-900">Who we share it with</h2>
          <ul className="mt-2 list-disc space-y-1.5 pl-5">
            <li><strong>Courier partners</strong> — the collection and delivery addresses and parcel details for a shipment are sent to the courier actually moving it, because that is what makes delivery possible.</li>
            <li><strong>BobPay</strong>, our payment processor, to take payment for wallet top-ups. We send them the amount and your email; your card details go directly to them, never through our servers.</li>
            <li><strong>Nominatim / OpenStreetMap</strong>, to turn an address you type into a location during address search. Only the text you search is sent, not your account identity.</li>
            <li><strong>Expo</strong>, to deliver push notifications to the mobile app. Only your device's push token is shared, not shipment or account content.</li>
            <li>Law enforcement or regulators, only where we are legally required to.</li>
          </ul>
          <p className="mt-2">We do not sell personal information to anyone, for any reason.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-ink-900">How long we keep it</h2>
          <p className="mt-2">
            We keep account, shipment, and financial records for as long as your account is active
            and for a reasonable period after, to meet tax, accounting, and dispute-resolution
            obligations. You can ask us to delete your account at any time; some records (ledger
            entries and completed shipments in particular) may be retained where we are legally
            required to keep them.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-ink-900">Your rights</h2>
          <p className="mt-2">
            Under South Africa's Protection of Personal Information Act (POPIA), you can ask us to
            confirm what personal information we hold about you, correct it if it's wrong, or delete
            it. Email <a className="text-brand" href={`mailto:${company.privacyEmail}`}>{company.privacyEmail}</a> and
            we will respond within a reasonable time.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-ink-900">Children</h2>
          <p className="mt-2">WeShip is not directed at children, and we do not knowingly collect information from anyone under 18.</p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-ink-900">Security</h2>
          <p className="mt-2">
            Passwords are hashed, not stored in plain text. Traffic between the app and our servers
            is encrypted (HTTPS/TLS). Access to customer data inside WeShip is limited to what a role
            actually needs to do its job.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-ink-900">Changes to this policy</h2>
          <p className="mt-2">
            If we make a material change to how we handle your information, we'll update the date at
            the top of this page and, where the change is significant, notify you directly.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-ink-900">Contact</h2>
          <p className="mt-2">
            {identityLine()} · <a className="text-brand" href={`mailto:${company.privacyEmail}`}>{company.privacyEmail}</a>
          </p>
        </section>
      </div>
    </div>
    <SiteFooter />
    </>
  );
}
